import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  advertisingApi,
  type AdjustAdvertisementSpendPayload,
  type AdvertisingCostPerLeadQuery,
  type CreateAdvertisementPayload,
  type RecordAdvertisementSpendPayload,
  type SaveAdvertisingBudgetPayload,
  type UpdateAdvertisementPayload,
} from "@/api/advertising";
import { advertisingKeys } from "./buyerKeys";
import { advertisingViewKeys } from "./advertisingViewKeys";

/**
 * Buyer advertising reads and writes (WS6).
 *
 * <p>🔴 <b>Every mutation invalidates the whole `advertisingKeys.all`
 * prefix</b>, and that is a decision rather than laziness. The five reads here
 * are five VIEWS OF ONE SET OF NUMBERS: recording a cost moves the
 * advertisement's row, its cost-per-lead, its team's utilization and the
 * cross-team roll-up simultaneously. Invalidating only the ledger would leave
 * the tile beside it showing the previous total for up to five minutes — the
 * QueryClient is `staleTime: 5min` with `refetchOnWindowFocus: false`, so a
 * missed invalidation has no recovery path and reads as a wrong number rather
 * than as a stale one.</p>
 */

export function useAdvertisingScope(enabled = true) {
  return useQuery({
    queryKey: advertisingViewKeys.scope(),
    queryFn: () => advertisingApi.scope(),
    enabled,
  });
}

export function useAdvertisements(teamId: string | null | undefined) {
  return useQuery({
    queryKey: advertisingKeys.ads(teamId ?? ""),
    queryFn: () => advertisingApi.advertisements(teamId!),
    enabled: !!teamId,
  });
}

/**
 * A team's whole budget history, newest month first.
 *
 * <p>The page picks the month it is showing out of this list rather than
 * calling the single-month endpoint. Two reasons: the factory names the key
 * `budgets(teamId)` and composing a month into that string would be a key no
 * invalidation could be reasoned about, and the history is worth having on the
 * screen anyway — "we spent 40% of budget" means little without last month
 * beside it.</p>
 */
export function useAdvertisingBudgets(teamId: string | null | undefined) {
  return useQuery({
    queryKey: advertisingKeys.budgets(teamId ?? ""),
    queryFn: () => advertisingApi.budgets(teamId!),
    enabled: !!teamId,
  });
}

export function useAdvertisementSpend(advertisementId: string | null | undefined) {
  return useQuery({
    queryKey: advertisingKeys.spend(advertisementId ?? ""),
    queryFn: () => advertisingApi.spend(advertisementId!),
    enabled: !!advertisementId,
  });
}

export function useAdvertisingRollup(period: string, enabled = true) {
  return useQuery({
    queryKey: advertisingKeys.rollup(period),
    queryFn: () => advertisingApi.rollup(period),
    enabled: enabled && !!period,
  });
}

/**
 * Every advertisement the caller may attribute a lead to — the source of the
 * intake "Advertisement" pickers.
 *
 * <p>`enabled` is the caller's `BuyerAdvertising.Read` answer. The endpoint
 * 403s for a caller without it (an F2 holds no advertising permission), so a
 * picker that fetched unconditionally would log a failed request on every
 * Quick add a specialist opened. The picker itself never renders for that
 * caller; this hook is what keeps the request from going out either.</p>
 */
export function useAttributableAdvertisements({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: advertisingKeys.attributable(),
    queryFn: () => advertisingApi.attributable(),
    enabled,
  });
}

/**
 * Month-bucketed spend, leads and budget pace for a window of months.
 *
 * <p>This is the ONLY read a one-team manager has that is bounded to a month:
 * `AdvertisementMetricsDto.spendToDate` is lifetime, and `rollup` is a
 * cross-team read gated `ManageAllTeams`. So "this team's spend against this
 * month's budget" — on the team tab and on the global page's month tiles —
 * comes from here, via the `budgets` rows. `from === to` asks for one month.</p>
 *
 * <p>`enabled` is the caller's `BuyerAdvertising.Read` answer AND whether the
 * team is in scope; a request for a team the scope did not name would 403.</p>
 */
export function useAdvertisingCostPerLead(
  query: AdvertisingCostPerLeadQuery,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: advertisingViewKeys.costPerLead(query.from, query.to, query.buyerTeamId ?? null),
    queryFn: () => advertisingApi.costPerLead(query),
    enabled: enabled && !!query.from && !!query.to,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** The one invalidation every advertising write issues — see the file note. */
function useInvalidateAdvertising() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: advertisingKeys.all });
}

export function useSaveAdvertisingBudget() {
  const invalidate = useInvalidateAdvertising();
  return useMutation({
    mutationFn: (payload: SaveAdvertisingBudgetPayload) =>
      advertisingApi.saveBudget(payload),
    onSuccess: () => invalidate(),
  });
}

export function useCreateAdvertisement() {
  const invalidate = useInvalidateAdvertising();
  return useMutation({
    mutationFn: (payload: CreateAdvertisementPayload) =>
      advertisingApi.createAdvertisement(payload),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAdvertisement() {
  const invalidate = useInvalidateAdvertising();
  return useMutation({
    mutationFn: (vars: { id: string; payload: UpdateAdvertisementPayload }) =>
      advertisingApi.updateAdvertisement(vars.id, vars.payload),
    onSuccess: () => invalidate(),
  });
}

export function useRecordAdvertisementSpend() {
  const invalidate = useInvalidateAdvertising();
  return useMutation({
    mutationFn: (vars: { id: string; payload: RecordAdvertisementSpendPayload }) =>
      advertisingApi.recordSpend(vars.id, vars.payload),
    onSuccess: () => invalidate(),
  });
}

export function useAdjustAdvertisementSpend() {
  const invalidate = useInvalidateAdvertising();
  return useMutation({
    mutationFn: (vars: { id: string; payload: AdjustAdvertisementSpendPayload }) =>
      advertisingApi.adjustSpend(vars.id, vars.payload),
    onSuccess: () => invalidate(),
  });
}
